import Button from "@/components/atoms/Button";
import { DatePicker } from "@/components/atoms/DatePicker";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Input from "@/components/atoms/Input";
import SowSchema from "@/schemas/sow";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { CheckboxButton } from "@/components/atoms/Checkbox";
import Sow from "@/types/Sow";
import Select from "@/components/molecules/Select";
import { Account } from "@/types/Account";
import Link from "@/components/atoms/Link";
import ErrorType from "@/types/ErrorType";
import { capitalize } from "@/lib/utils";
import type { NestedList, NestedItem } from "@/types/NestedItem";
import FileUpload from "@/components/atoms/FileUpload";
import axios from 'axios';
import { FieldValues } from 'react-hook-form';
import { useState } from 'react';
import useAccess from "@/hooks/useAccess";
import * as Constants from "@/constants";
import { toUtcDate } from "@/lib/utils";

export type SowProps = {
  accounts: Account[];
  functions: NestedList[];
  departments: NestedList[];
  countries: NestedList[];
  entities: NestedItem[];
  nmq_entities: NestedItem[];
  payment_types: string[];
  payment_systems: string[];
  currencies: string[];
  sows: any[];
};

type Props = {
  mode?: "create" | "edit";
  sow: Sow | null;
  edit: boolean;
  onSubmit: (data: Sow) => void;
  data: SowProps;
  loading: boolean;
  form: UseFormReturn<any>;
  errors?: ErrorType;
  unfreezeSow?: boolean;
};
interface HandleAccountChangeParams {
  value: string;
  field: FieldValues;
  data: SowProps;
  setData: React.Dispatch<React.SetStateAction<SowProps>>;
}

export const useSowForm = (sow: Sow | null) =>
  
  useForm({
    resolver: zodResolver(SowSchema),
    defaultValues: sow
      ? {
          ...sow,
          account_id: sow.account.id.toString(),
          entity_id: sow.entity?.id?.toString() || "",
          nmq_entity_id: sow.nmq_entity?.id?.toString() || "",
          master_sow_id: sow.master_sow?.id?.toString() || "",
          parent_id: sow.parent?.id?.toString() || "",
          function_ids: sow.functions?.map((obj) => obj.id.toString()) || [],
          department_ids: sow.departments?.map((obj) => obj.id.toString()) || [],
          country_ids: sow.region_id != null 
            ? (sow.countries?.[0]?.children?.map((child) => child.id.toString()) || []) 
            : (sow.countries?.map((obj) => obj.id.toString()) || []),
          region_id: sow.region_id?.toString() || "",
          start_date: toUtcDate(sow.start_date),
          end_date: toUtcDate(sow.end_date),
        }
      : {
          account_id: "",
          internal_approval: false,
          internal_approval_person: "",
          external_approval: false,
          external_approval_person: "",
          function_ids: [],
          department_ids: [],
          country_ids: [],
          region_id: "",
          entity_id: "",
          nmq_entity_id: "",
          currency: "",
          master_sow_id: "",
          parent_id: "",
          payment_type: "",
          payment_system: "",
          po_number: "",
          details: "",
          short_description: "",
          renewed: false,
          start_date: "",
          end_date: "",
          is_package: false,
          package_name: "",
        },
  });

  
const SowForm = ({
  form,
  mode = "edit",
  edit,
  onSubmit,
  sow,
  data,
  loading,
  errors,
  unfreezeSow = false,
}: Props) => {
  const { getAccess } = useAccess();
  const [formData, setFormData] = useState(data);
  const [invoicedPeriods, setInvoicedPeriods] = useState<string[]>(sow?.invoiced_periods || []);
  
  const handleSubmit = (data: any) => {
    const payload = {
      sow: {
        ...data,
        parent_id: data.renewed ? data.parent_id : null,
        periods: getAccess(["set_sow_periods"]) ? data.periods : [],
        start_date: formatToISO(data.start_date),
        end_date: formatToISO(data.end_date),
      },
      files: (form.getValues("files") || []).map((file: any) => ({
        filename: file.name,
        filesize: file.size,
        filetype: file.content_type,
        filepath: file.path,
        original_name: file.original_name,
        fileurl: file.url,
      })),
    };

    onSubmit(payload as any);
  };

  useEffect(() => {
    if (!edit && sow)
      form.reset({
        ...sow,
        entity_id: sow?.entity?.id?.toString() || "",
        nmq_entity_id: sow?.nmq_entity?.id.toString() || "",
        master_sow_id: sow?.master_sow?.id.toString() || "",
        parent_id: sow?.parent?.id.toString() || "",
        function_ids: sow?.functions?.map((obj) => obj.id.toString()) || [],
        department_ids: sow?.departments?.map((obj) => obj.id.toString()) || [],
        country_ids: sow.region_id != null
        ? (sow.countries?.[0]?.children?.map((child) => child.id.toString()) || [])
        : (sow.countries?.map((obj) => obj.id.toString()) || []),
        region_id: sow.region_id?.toString() || "",
        files: [],
        periods: sow?.periods || [],
        start_date: toUtcDate(sow.start_date),
        end_date: toUtcDate(sow.end_date),
      });
  }, [edit, sow]);

  useEffect(() => {
    if (sow) {
      form.reset({
        ...sow,
        entity_id: sow?.entity?.id?.toString() || "",
        nmq_entity_id: sow?.nmq_entity?.id.toString() || "",
        master_sow_id: sow?.master_sow?.id.toString() || "",
        parent_id: sow?.parent?.id.toString() || "",
        function_ids: sow?.functions?.map((obj) => obj.id.toString()) || [],
        department_ids: sow?.departments?.map((obj) => obj.id.toString()) || [],
        country_ids: sow.region_id != null
        ? (sow.countries?.[0]?.children?.map((child) => child.id.toString()) || [])
        : (sow.countries?.map((obj) => obj.id.toString()) || []),
        region_id: sow.region_id?.toString() || "",
        files: [],
        periods: sow?.periods || [],
        start_date: toUtcDate(sow.start_date),
        end_date: toUtcDate(sow.end_date),
      });
    }
  }, [sow]);

  const formatServicePeriod = (servicePeriod :string) => {
    const year = servicePeriod.slice(0, 4);
    const month = servicePeriod.slice(4, 6);
    const date = toUtcDate(`${year}-${month}-01`);

    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
  };

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);

  async function handleAccountChange({ value, field, data, setData }: HandleAccountChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/accounts/${value}/read_account`);

    setData({
      ...data,
      entities: response.data.entities,
      functions: response.data.functions,
      departments: response.data.departments,
      countries: response.data.countries,
      sows: response.data.sows,
    });
  }

  async function handleEntityChange({ value, field, data, setData }: HandleAccountChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/entities/${value}/read_entity`);

    setData({
      ...data,
      nmq_entities: response.data.nmq_entities
    });

    
  }

  const formatToISO = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
  
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    const adjustedDate = new Date(date.getTime() - timezoneOffsetMs);
  
    return adjustedDate.toISOString();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-8 w-full"
      >
        
        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="account_id"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Account*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.accounts.map((account) => ({
                      label: account.name,
                      value: account.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) => handleAccountChange({ value, field, data: formData, setData: setFormData })}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                    enabledSearch={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="entity_id"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.entities.map((entity) => ({
                      label: entity.name,
                      value: entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) =>  handleEntityChange({ value, field, data: formData, setData: setFormData })}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nmq_entity_id"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>NMQ Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.nmq_entities.map((nmq_entity) => ({
                      label: nmq_entity.name,
                      value: nmq_entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex gap-12">
        <FormField
            control={form.control}
            name="function_ids"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Functions</FormLabel>
                <FormControl>
                  <Select
                    multiple
                    options={
                      formData.functions.map((func) => ({
                        label: func.name,
                        value: func.id.toString(),
                      })) || []
                    }
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="department_ids"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Departments</FormLabel>
                <FormControl>
                  <Select
                    multiple
                    options={
                      formData.departments.map((department) => ({
                        label: department.name,
                        value: department.id.toString(),
                      })) || []
                    }
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country_ids"
            render={({ field }) => {
              const regionWithChildren = formData.countries.find(
                (country) => country.children && country.children.length > 0
              );

              const selectedRegionId = form.watch("region_id");

              return (
                <>
                  {!regionWithChildren && (
                    <FormItem className="w-1/5">
                      <FormLabel>Countries</FormLabel>
                      <FormControl>
                        <Select
                          multiple
                          options={formData.countries.map((country) => ({
                            label: country.name,
                            value: country.id.toString(),
                          }))}
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          disabled={!edit}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}

                  {regionWithChildren && (
                    <>
                      <FormField
                        control={form.control}
                        name="region_id"
                        render={({ field: regionField }) => (
                          <FormItem className="w-1/5">
                            <FormLabel>Region</FormLabel>
                            <FormControl>
                              <Select
                                options={[
                                  { label: "Please Select", value: "null" }, // Seçimi kaldırmak için bir seçenek
                                  ...formData.countries.map((country) => ({
                                    label: country.name,
                                    value: country.id.toString(),
                                  })),
                                ]}
                                value={regionField.value}
                                onChange={(selectedOption) => {
                                  if (selectedOption === "null") {
                                    form.setValue("country_ids", []);
                                  }
                                  
                                  regionField.onChange(
                                    selectedOption === "null" ? null : selectedOption
                                  );
                                }}
                                disabled={!edit}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {selectedRegionId && (
                      <FormItem className="w-1/5">
                        <FormLabel>Countries</FormLabel>
                        <FormControl>
                          <Select
                            multiple
                            options={
                              formData.countries
                                .find(
                                  (country) =>
                                    country.id.toString() === selectedRegionId
                                )
                                ?.children?.map((child) => ({
                                  label: child.name,
                                  value: child.id.toString(),
                                })) || []
                            }
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            disabled={!edit}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                      )}
                    </>
                  )}
                </>
              );
            }}
          />

        </div>
        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Currency*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.currencies.map((currency) => ({
                      label: currency,
                      value: currency,
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="payment_type"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Payment Type*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.payment_types.map((payment_type) => ({
                      label: payment_type,
                      value: payment_type,
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="payment_system"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Payment System*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.payment_systems.map((payment_system) => ({
                      label: payment_system,
                      value: payment_system,
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit ? true : ((!unfreezeSow && (sow?.invoiced_periods || []).length > 0) ? true : false)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="details"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Details</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="short_description"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Short Description</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Short Description"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="po_number"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>PO Number</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Po Number"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="master_sow_id"
            render={({ field }) => (
              (mode === "create" || (mode === "edit" && sow?.master_sow !== null)) ? (
                <FormItem className="w-1/5">
                  <FormLabel>Master SOW</FormLabel>
                  <FormControl>
                    <Select
                      options={formData.sows.map((sow) => ({
                        label: sow.display_id,
                        value: sow.id.toString(),
                      }))}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      enabledSearch={true}
                      disabled={mode === "edit" && sow?.master_sow !== null}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              ) : (
                <></>
              )
            )}
          />

          <FormField
              control={form.control}
              name="is_package"
              render={({ field }) => (
                <FormItem className="w-1/5 h-1/5 max-w-44 mt-6">
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Is Package
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.getValues("is_package") && (
              <FormField
                control={form.control}
                name="package_name"
                render={({ field }) => (
                  <FormItem className="w-1/5">
                    <FormLabel>Package Name</FormLabel>
                    <FormControl>
                      <Select
                        options={Constants.SOW_PACKAGE_NAMES.map((packageName) => ({
                          label: packageName,
                          value: packageName,
                        }))}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        disabled={!edit}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
        </div>

        <div className="flex gap-12">
          <div className="shadow p-2 w-full">
            {edit && getAccess(["set_sow_periods"]) && (sow?.periods ?? []).length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold">Periods</h3>
                <div className="flex flex-wrap gap-12">
                  {sow?.periods?.map((period, index) => (
                    <div key={index} className="border p-2 rounded-lg shadow-sm w-1/5">
                      <h4 className="text-lg font-semibold text-center">{formatServicePeriod(period.service_period)}</h4>
                      <FormField
                        control={form.control}
                        name={`periods[${index}].service_period`}
                        render={({ field }) => (
                          <FormItem className="w-1/5">
                            <FormControl>
                              <Input
                                type="hidden"
                                value={period.service_period}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`periods[${index}].is_it_manual`}
                        render={({ field }) => (
                          <FormItem className="w-1/5">
                            <FormControl>
                              <Input
                                type="hidden"
                                value={period.is_it_manual ? 'true' : 'false'}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`periods[${index}].amount`}
                        render={({ field }) => (
                          <FormItem >
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                {...field}
                                value={field.value || period.amount || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                                    field.onChange(e);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`periods[${index}].description`}
                        render={({ field }) => (
                          <FormItem className="mt-2">
                            <FormControl>
                              <Input {...field} value={field.value || period.description || ""} placeholder= "Description"/>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
          
        
        <div className="flex gap-12">
          <FormField
              control={form.control}
              name="renewed"
              render={({ field }) => (
                <FormItem className="w-1/5 h-1/5 max-w-44 mt-6">
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Is Renewed
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          { form.getValues("renewed") && (
            <FormField
              control={form.control}
              name="parent_id"
              render={({ field }) => (
                <FormItem className="w-1/5">
                  <FormLabel>Renewed SOW</FormLabel>
                  <FormControl>
                    <Select
                      options={formData.sows.map((sow) => ({
                        label: sow.display_id,
                        value: sow.id.toString(),
                      }))}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      disabled={!edit}
                      enabledSearch={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="internal_approval"
            render={({ field }) => (
              <FormItem className="w-1/5 h-1/5 max-w-44 mt-6">
                <FormControl>
                  <CheckboxButton
                    disabled={!edit}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  >
                    Internal Approval
                  </CheckboxButton>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          { form.getValues("internal_approval") && (
            <FormField
              control={form.control}
              name="internal_approval_person"
              render={({ field }) => (
                <FormItem className="w-1/5">
                  <FormLabel>Internal Approved By</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!edit} placeholder= "Internal Approved By"/>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          
        </div>
        <div className="flex gap-12">
          <FormField
              control={form.control}
              name="external_approval"
              render={({ field }) => (
                <FormItem className="w-1/5 h-1/5 max-w-44 mt-6">
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      External Approval
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          { form.getValues("external_approval") && (
            <FormField
              control={form.control}
              name="external_approval_person"
              render={({ field }) => (
                <FormItem className="w-1/5">
                  <FormLabel>External Approved By</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!edit} placeholder= "External Approved By"/>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="py-2">
          <div className="flex flex-wrap gap-3 mt-2">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <DatePicker 
                      placeholder="Start Date"
                      value={field.value ? toUtcDate(field.value) : undefined}
                      onChange={(value) => {
                        if (value) {
                          field.onChange(toUtcDate(value, true));
                        }
                      }}
                      disabled={!edit}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      placeholder="End Date"
                      value={field.value ? toUtcDate(field.value) : undefined}
                      onChange={(value) => {
                        if (value) {
                          field.onChange(toUtcDate(value, true));
                        }
                      }}
                      disabled={!edit}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />


          </div>
        </div>
        
        <div className="py-2">
          <div className="flex flex-wrap gap-3 mt-2">
            {edit ? (
              <FormField
                control={form.control}
                name="files[]"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Attachments</FormLabel>
                    <FormControl>
                      <FileUpload
                        files={field.value || []}
                        onChange={(files) => field.onChange(files)}
                        disabled={!edit}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <div>
                <h3>Attachments:</h3>
                <ul>
                  {sow?.attachments?.map((attachment, index) => (
                    <li key={index}>
                      <a href={attachment.url || ""} download>
                        {attachment.original_name || attachment.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
              
          </div>
        </div>
            
        
        
        <div className="flex gap-2">
          {mode === "create" && (
            <Link href="/sows">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit || loading}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default SowForm;
